import { intlTag, baseLocale } from "@/lib/i18n/translations";
import type { Locale } from "@/lib/i18n/translations";
export const SITE = {
  name: "Danish Wedding",
  tagline: "Heiraten in Dänemark. Klar begleitet bis zur Urkunde.",
  /** Production domain as printed in legal texts ("wedding-denmark.com/vertrag-widerrufen"),
   *  independent of NEXT_PUBLIC_SITE_URL, which is localhost in development. */
  domain: "wedding-denmark.com",
  email: "info@wedding-denmark.com",
  /** Data-protection / subject-access mailbox (same inbox as general contact). */
  privacyEmail: "info@wedding-denmark.com",
  phone: "+41 76 345 31 73",
  phoneRaw: "+41763453173",
  whatsappNumber: "41763453173",
  /**
   * Telegram: Alexander's public username (operator, 28.09.2026). A username
   * link always opens the chat; the earlier t.me/+<number> link only worked if
   * the number was findable and it exposed the number.
   */
  telegramUrl: "https://t.me/alexanderkoltman",
  telegramHandle: "@alexanderkoltman",
  whatsappPrefilledMessage:
    "Hallo! Ich interessiere mich für eine Hochzeit in Dänemark. Könnt ihr mir sagen, ob mein Fall bearbeitbar ist?",
  /** Cal.com URL for the 15-min consultation; override per environment via NEXT_PUBLIC_BOOKING_URL. */
  bookingUrl:
    process.env.NEXT_PUBLIC_BOOKING_URL ?? "https://cal.com/wedding-denmark/15min",
  social: {
    instagram: "https://www.instagram.com/danish_wedding/",
    tiktok: "https://www.tiktok.com/@danish.wedding",
    // The Facebook page has had the username danish.wedding since 25.10.2026;
    // the old /people/… and profile.php?id= URLs redirect here. Keep it equal
    // to the sameAs entry in layout.tsx.
    facebook: "https://www.facebook.com/danish.wedding/",
    pinterest: "https://www.pinterest.com/danishwedding/",
  },
} as const;

/**
 * Provider identity used by the imprint, right-of-withdrawal and privacy pages.
 * `companyLine` is the legal firm name of the sole proprietorship: Swiss CO
 * Art. 945 requires it to contain the owner's family name, so a bare
 * "Danish Wedding" is not permissible as a firm name (that stays the brand,
 * SITE.name). `operatorName` is the owner's full name, mandatory in the imprint.
 */
export const LEGAL = {
  companyLine: "Danish Wedding Koltman",
  operatorName: "Alexander Koltman",
  street: "Fluhmattstrasse 31",
  zipCity: "5400 Baden",
  country: "Schweiz",
  /** Place of jurisdiction per T&C §7; mandatory consumer law may override it. */
  jurisdictionCity: "Baden",
  /**
   * EU representative under GDPR Art. 27. The controller is based in
   * Switzerland (third country) and offers services to data subjects in the EU
   * while regularly processing ID and divorce documents, so the Art. 27(2)
   * exemption does not apply. Fill in name, full EU address and email once a
   * representative is appointed; the privacy policy renders the block
   * automatically (Art. 13(1)(a)). Empty means not yet appointed.
   */
  euRepresentative: {
    name: "",
    address: "",
    email: "",
  },
} as const;

/**
 * Version date ("Stand") of each legal text: the month of its last change in
 * wording, as "YYYY-MM". Fixed on purpose: a date computed from today would
 * claim a new revision every month although nothing changed. Update an entry
 * in the same change that edits the text; layout or typo fixes do not count.
 */
/** Google Ads conversion measurement; the tag loads only after consent (components/GoogleAdsTag). */
export const GOOGLE_ADS_ID = "AW-18325210233";
/**
 * Google Analytics 4 property ("G-…"), only after consent in the cookie banner
 * (operator, 11.10.2026). Empty until the property exists: then the banner,
 * the tag and the privacy policy say nothing about Analytics.
 */
export const GA4_ID = process.env.NEXT_PUBLIC_GA4_ID?.trim() || "";

export const LEGAL_STAND = {
  impressum: "2026-09",
  agb: "2026-10",
  datenschutz: "2026-10",
  widerruf: "2026-09",
  vollmacht: "2026-09",
} as const;

export type LegalDocument = keyof typeof LEGAL_STAND;

/** Legal texts that also exist as PDF (printed from the pages by
 *  scripts/legal-pdfs.mjs) and go with the order confirmation. */
export type LegalPdfDocument = "agb" | "widerruf";

/**
 * Public path of such a PDF, e.g. "/legal/widerruf-2026-09-de.pdf". The Stand
 * is part of the name, so a new wording gets a new file and an order
 * confirmation can never attach an older text under a current name: after a
 * LEGAL_STAND bump, run the script again.
 */
export function legalPdfPath(doc: LegalPdfDocument, locale: Locale): string {
  // The legal PDFs exist in German, English and Danish; Arabic and Ukrainian
  // readers get the English one.
  return `/legal/${doc}-${LEGAL_STAND[doc]}-${baseLocale(locale)}.pdf`;
}

/** "September 2026" / "september 2026", in the reader's language. */
export function legalStand(doc: LegalDocument, locale: Locale): string {
  const [year, month] = LEGAL_STAND[doc].split("-").map(Number);
  const tag = intlTag(locale);
  // Mid-month in UTC, so no time zone can shift it into another month.
  return new Date(Date.UTC(year, month - 1, 15)).toLocaleDateString(tag, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

/** Localized WhatsApp draft messages so EN/DA visitors do not open a German chat. */
const WHATSAPP_PREFILL: Record<Locale, string> = {
  de: SITE.whatsappPrefilledMessage,
  en: "Hi! I'm interested in getting married in Denmark. Could you tell me whether my case is feasible?",
  da: "Hej! Jeg er interesseret i at blive gift i Danmark. Kan I fortælle mig, om min sag kan behandles?",
  ar: "مرحبًا! يهمّني الزواج في الدنمارك. هل يمكنكم إخباري ما إذا كانت حالتي ممكنة؟",
  uk: "Добрий день! Мене цікавить одруження в Данії. Чи могли б ви підказати, чи можливо це в моєму випадку?",
  ru: "Здравствуйте! Меня интересует брак в Дании. Подскажите, возможен ли он в моём случае?",
  vi: "Xin chào! Tôi quan tâm đến việc kết hôn tại Đan Mạch. Bạn có thể cho tôi biết trường hợp của tôi có thực hiện được không?",
  es: "¡Hola! Me interesa casarme en Dinamarca. ¿Podrían decirme si mi caso es viable?",
};

/** "(Partner-Code LENA)" in the chat's language; it was German in every one. */
const PARTNER_CODE_LABEL: Record<Locale, string> = {
  de: "Partner-Code",
  en: "partner code",
  da: "partnerkode",
  ar: "رمز الشريك",
  uk: "код партнера",
  ru: "код партнёра",
  vi: "mã đối tác",
  es: "código de socio",
};

export function whatsappLink(message?: string, locale?: Locale, partnerCode?: string | null) {
  const base = message ?? WHATSAPP_PREFILL[locale ?? "de"] ?? SITE.whatsappPrefilledMessage;
  // On a partner page the chat names the code, so the lead is credited. The
  // code stays in capitals, as the partner and the admin know it.
  const code = partnerCode ? `(${PARTNER_CODE_LABEL[locale ?? "de"]} ${partnerCode.toUpperCase()})` : "";
  const text = encodeURIComponent(code ? `${base} ${code}` : base);
  return `https://wa.me/${SITE.whatsappNumber}?text=${text}`;
}

export function telLink() {
  return `tel:${SITE.phoneRaw}`;
}

/**
 * When a person is actually at the other end of the chat channels. The site
 * says "Mo–So 8–22" in several places; this is that promise as code, so a
 * status dot can tell the truth instead of glowing around the clock.
 *
 * Swiss time, because that is where the answering happens.
 */
export const SUPPORT_HOURS = { start: 8, end: 22, timeZone: "Europe/Zurich" } as const;

/** True while someone is reachable. Client-side only — it reads the clock. */
export function isSupportOpen(now: Date = new Date()) {
  // formatToParts, not format(): a German locale renders the hour as "14 Uhr",
  // and Number("14 Uhr") is NaN, which would keep the dot off around the clock.
  const teil = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    hour12: false,
    timeZone: SUPPORT_HOURS.timeZone,
  })
    .formatToParts(now)
    .find((t) => t.type === "hour");
  if (!teil) return false;
  const stunde = Number(teil.value);
  if (!Number.isFinite(stunde)) return false;
  return stunde >= SUPPORT_HOURS.start && stunde < SUPPORT_HOURS.end;
}

export function telegramLink() {
  return SITE.telegramUrl;
}

export function mailLink(subject?: string) {
  const sub = subject ? `?subject=${encodeURIComponent(subject)}` : "";
  return `mailto:${SITE.email}${sub}`;
}

/**
 * Entry point for "book a consultation". Deliberately points at the internal
 * /beratung page (which collects basic case data first) rather than Cal.com;
 * /beratung then forwards to the real calendar via calBookingUrl().
 */
export function bookingLink() {
  return "/beratung";
}

/**
 * The actual Cal.com calendar URL. Only used by /beratung after the pre-check
 * form was submitted; optional prefill values populate the Cal.com form.
 */
export function calBookingUrl(prefill?: { name?: string; email?: string; notes?: string }) {
  if (!prefill) return SITE.bookingUrl;
  const qs = new URLSearchParams();
  if (prefill.name) qs.set("name", prefill.name);
  if (prefill.email) qs.set("email", prefill.email);
  if (prefill.notes) qs.set("notes", prefill.notes);
  const q = qs.toString();
  return q ? `${SITE.bookingUrl}?${q}` : SITE.bookingUrl;
}
